import { Component, OnInit, Input, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DatePipe } from '@angular/common';
import { GuestGeneralInfoService } from './guest-general-info.service';
import { ImsService } from '../../ims.service';
import { ToasterService } from 'src/app/toaster.service';
import { Toaster, ToasterType } from 'src/app/shared/types';
import {
  resolveDomainValidationErrorMessage,
  resolveDomainValidationSuccessMessage,
} from 'src/app/shared/domain-validation/domain-validation-messages';

const GUEST_PROFILE_TYPE = 'guest';

@Component({
    selector: 'app-guest-general-info',
    templateUrl: './guest-general-info.component.html',
    styleUrls: ['./guest-general-info.component.css'],
    providers: [DatePipe],
})
export class GuestGeneralInfoComponent implements OnInit, OnChanges {

    @Input() data: any;
    public info: any = {};
    public referralSummary: any = {};
    public lifetimeRides: any = null;
    public guestDetails: any = null;
    public showEmailDialog = false;
    public showDeleteEmailDialog = false;
    public newEmail = '';
    public updatingEmail = false;
    public deletingEmail = false;
    public errorMsg = '';
    public domainType = '';
    public domainTypeLookupLoading = false;
    public readonly domainTypeOptions = ['corporate', 'non_corporate'];
    public deleteErrorMsg = '';
    public showNameDialog = false;
    public newName = '';
    public updatingName = false;
    public nameErrorMsg = '';
    constructor(
        public dialog: MatDialog,
        private guestGeneralInfoService: GuestGeneralInfoService,
        private imsService: ImsService,
        private toasterService: ToasterService,
        private datePipe: DatePipe,
    ) { }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.data && changes.data.currentValue) {
            this.data = changes.data.currentValue;
            if (this.data.dataFetched) {
                this.fetchReferralSummaryAndConstruct();
            } else {
                this.blankCard();
            }
        } else {
            this.emptyCard();
        }
    }

    ngOnInit() { }

    blankCard() {
        this.info = {
            header: 'General Info',
            data: [],
            dataFetched: false
        };
    }

    emptyCard() {
        this.info = {
            header: 'General Info',
            dataFetched: false,
            data: null
        };
    }

    fetchReferralSummaryAndConstruct() {
        const guestProfileData = this.data.profiles.filter(profile => profile.type === GUEST_PROFILE_TYPE)[0];
        if (!guestProfileData) {
            this.blankCard();
            return;
        }
        let referralComplete = false;
        this.guestDetails = null;
        this.guestGeneralInfoService.getGuestDetails(guestProfileData.id).subscribe(
            (result: { guests?: any[] }) => {
                this.guestDetails = result && result.guests && result.guests.length > 0 ? result.guests[0] : null;
                if (referralComplete) {
                    this.constructCard();
                }
            },
            error => {
                console.error('Error fetching guest details:', error);
                this.guestDetails = null;
                if (referralComplete) {
                    this.constructCard();
                }
            }
        );
        this.guestGeneralInfoService.getReferralSummary(guestProfileData.id).subscribe(
            (result: any) => {
                this.referralSummary = result || {};
                referralComplete = true;
                this.constructCard();
            },
            error => {
                console.error('Error fetching referral summary:', error);
                this.referralSummary = {};
                referralComplete = true;
                this.constructCard();
            }
        );
        this.guestGeneralInfoService.getLifetimeRides(guestProfileData.id).subscribe(
            (result: any) => {
                this.lifetimeRides = result || null;
                if (referralComplete) {
                    this.constructCard();
                }
            },
            error => {
                console.error('Error fetching lifetime rides:', error);
                this.lifetimeRides = null;
                if (referralComplete) {
                    this.constructCard();
                }
            }
        );
    }

    constructCard() {
        if (this.data && this.data.dataFetched) {
            const guestProfileData = this.data.profiles.filter(profile => profile.type === GUEST_PROFILE_TYPE)[0];
            if (!guestProfileData) {
                this.blankCard();
                return;
            }
            const cityName = this.getCityName();
            this.info = {
                header: 'General Info',
                dataFetched: true,
                data: [
                    {
                        header: 'Name',
                        value: this.data.name,
                        className: 'id-icon',
                        svgIcon: true,
                        actionMenuItems: [
                            { label: 'Edit Name', event: 'Edit Name' }
                        ]
                    },
                    { header: 'Gender', value: this.data.gender, className: 'id-icon', svgIcon: true },
                    ...(cityName ? [{ header: 'City', value: cityName, className: 'id-icon', svgIcon: true }] : []),
                    {
                        header: 'Email',
                        value: guestProfileData.email,
                        className: 'email-id-icon',
                        svgIcon: true,
                        actionMenuItems: this.getEmailActionMenuItems(guestProfileData.email)
                    },
                    { header: 'LTR', value: this.getLtrValue(), className: 'id-icon', svgIcon: true },
                    ...(this.referralSummary.myReferralCode ? [{ header: 'My Referral Code', value: this.referralSummary.myReferralCode, svgIcon: true, className: 'referral-code-icon' }] : []),
                    ...(this.referralSummary.referredBy && this.referralSummary.referredBy.name ? [{ header: 'Referred By', value: this.referralSummary.referredBy.mobile ? `${this.referralSummary.referredBy.name} (${this.referralSummary.referredBy.mobile})` : this.referralSummary.referredBy.name, svgIcon: true, className: 'referral-code-icon', ...(this.referralSummary.referredBy.role && this.referralSummary.referredBy.mobile ? { redirectLink: `/${this.referralSummary.referredBy.role.toLowerCase()}/${this.referralSummary.referredBy.mobile}` } : {}) }] : []),
                    ...(this.referralSummary.referredBy && this.referralSummary.referredBy.code ? [{ header: 'Referred By Code', value: this.referralSummary.referredBy.code, svgIcon: true, className: 'referral-code-icon' }] : []),
                    ...(this.referralSummary.referredAt ? [{ header: 'Referred At', value: this.formatDate(this.referralSummary.referredAt), svgIcon: true, className: 'referral-code-icon' }] : []),
                    ...(this.referralSummary.referredRewardAchievedAt ? [{ header: 'Referral Reward At', value: this.formatDate(this.referralSummary.referredRewardAchievedAt), svgIcon: true, className: 'referral-code-icon' }] : []),
                ]
            };
        }
    }

    formatDate(dateStr: string): string {
        if (!dateStr) { return 'N/A'; }
        return this.datePipe.transform(dateStr, 'mediumDate') || 'N/A';
    }

    getCityName(): string {
        const guest = this.guestDetails;
        if (!guest) {
            return '';
        }
        const cityData = guest.cityData || guest.city || (guest.profileData && (guest.profileData.cityData || guest.profileData.city));
        if (!cityData) {
            return '';
        }
        if (typeof cityData === 'string') {
            return cityData;
        }
        return cityData.name || cityData.displayName || cityData.cityName || '';
    }

    getLtrValue(): string {
        if (this.lifetimeRides && typeof this.lifetimeRides.totalDroppedRides === 'number') {
            return String(this.lifetimeRides.totalDroppedRides);
        }
        return 'N/A';
    }

    getEmailActionMenuItems(email: string) {
        const menuItems = [{ label: 'Edit Email', event: 'Edit Email' }];
        if (email) {
            menuItems.push({ label: 'Delete Email', event: 'Delete Email' });
        }
        return menuItems;
    }

    eventTriggered(value: string) {
        if (value === 'Edit Email') {
            this.errorMsg = '';
            this.newEmail = '';
            this.resetDomainTypeSelection();
            this.showEmailDialog = true;
        } else if (value === 'Delete Email') {
            this.deleteErrorMsg = '';
            this.showDeleteEmailDialog = true;
        } else if (value === 'Edit Name') {
            this.nameErrorMsg = '';
            this.newName = '';
            this.showNameDialog = true;
        }
    }

    closeEmailDialog() {
        if (this.updatingEmail) {
            return;
        }
        this.showEmailDialog = false;
        this.newEmail = '';
        this.resetDomainTypeSelection();
        this.errorMsg = '';
    }

    resetDomainTypeSelection() {
        this.domainType = '';
        this.domainTypeLookupLoading = false;
    }

    canLookupEmail(): boolean {
        const trimmedEmail = this.newEmail ? this.newEmail.trim() : '';
        return trimmedEmail.indexOf('@') > 0;
    }

    lookupEmailDomain() {
        const trimmedEmail = this.newEmail ? this.newEmail.trim() : '';
        if (!trimmedEmail || trimmedEmail.indexOf('@') <= 0) {
            this.resetDomainTypeSelection();
            return;
        }

        this.domainTypeLookupLoading = true;
        this.imsService.lookupEmailDomain(trimmedEmail).subscribe({
            next: (response: any) => {
                this.domainTypeLookupLoading = false;
                if (response && response.exists && response.domainType) {
                    this.domainType = response.domainType;
                } else {
                    this.domainType = '';
                }
            },
            error: () => {
                this.domainTypeLookupLoading = false;
                this.domainType = '';
            }
        });
    }

    submitEmail() {
        if (!this.data || !this.data.profiles) {
            return;
        }
        const guestProfile = this.data.profiles.find(profile => profile.type === GUEST_PROFILE_TYPE);
        const trimmedEmail = this.newEmail ? this.newEmail.trim() : '';
        if (!guestProfile || !trimmedEmail) {
            this.errorMsg = 'Please enter a valid email.';
            return;
        }
        if (!this.domainType) {
            this.errorMsg = 'Please select a domain type.';
            return;
        }

        this.updatingEmail = true;
        this.errorMsg = '';
        this.imsService.updateUserEmail(this.data.id, trimmedEmail, [GUEST_PROFILE_TYPE], this.domainType).subscribe({
            next: (res) => {
                guestProfile.email = trimmedEmail;
                this.constructCard();
                this.showEmailDialog = false;
                this.newEmail = '';
                this.updatingEmail = false;
                const successCopy = resolveDomainValidationSuccessMessage(res) ||
                    'Email updated successfully.';
                this.toasterService.showToaster(new Toaster({
                    type: ToasterType.SUCCESS,
                    message: successCopy
                }));
            },
            error: (err) => {
                this.updatingEmail = false;
                this.errorMsg = resolveDomainValidationErrorMessage(err);
            }
        });
    }

    closeDeleteEmailDialog() {
        if (this.deletingEmail) {
            return;
        }
        this.showDeleteEmailDialog = false;
        this.deleteErrorMsg = '';
    }

    deleteEmail() {
        if (!this.data || !this.data.id) {
            return;
        }
        this.deletingEmail = true;
        this.deleteErrorMsg = '';
        this.imsService.deleteUserEmail(this.data.id).subscribe({
            next: () => {
                this.showDeleteEmailDialog = false;
                this.deletingEmail = false;
                this.toasterService.showToaster(new Toaster({
                    type: ToasterType.SUCCESS,
                    message: 'Email deleted successfully.'
                }));
                window.location.reload();
            },
            error: (err) => {
                this.deletingEmail = false;
                this.deleteErrorMsg = (err && err.error && err.error.metadata && err.error.metadata.error &&
                    err.error.metadata.error.displayMessage) || 'Failed to delete email';
            }
        });
    }

    closeNameDialog() {
        if (this.updatingName) {
            return;
        }
        this.showNameDialog = false;
        this.newName = '';
        this.nameErrorMsg = '';
    }

    submitName() {
        if (!this.data) {
            return;
        }
        if (!this.newName || !this.newName.trim()) {
            this.nameErrorMsg = 'Please enter a valid name.';
            return;
        }

        this.updatingName = true;
        this.nameErrorMsg = '';
        this.imsService.updateUserName(this.data.id, this.newName.trim()).subscribe({
            next: () => {
                this.data.name = this.newName.trim();
                this.constructCard();
                this.showNameDialog = false;
                this.newName = '';
                this.updatingName = false;
                this.toasterService.showToaster(new Toaster({
                    type: ToasterType.SUCCESS,
                    message: 'Name updated successfully.'
                }));
            },
            error: (err) => {
                this.updatingName = false;
                this.nameErrorMsg = (err && err.error && err.error.metadata && err.error.metadata.error &&
                    err.error.metadata.error.displayMessage) || 'Failed to update name';
            }
        });
    }

    accessCookie(cookieName) {
        const name = cookieName + '=';
        const allCookieArray = document.cookie.split(';');
        for (let i = 0; i < allCookieArray.length; i++) {
            const temp = allCookieArray[i].trim();
            if (temp.indexOf(name) === 0) {
                return temp.substring(name.length, temp.length);
            }
        }
        return '';
    }
}
